/**
 * MonthlyCalendarView - Visualização do calendário mensal de sangria
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Grid de calendário com dias do mês
 * - Exibição de atividades diárias (sangria, reposição, fungicida, ethrel)
 * - Indicador de múltiplos trabalhadores por dia
 * - Tooltip com detalhes do dia no hover
 * - Produção total do dia
 * - Integração com RecordFormDialog para adicionar/editar registros
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - Plus (lucide-react)
 * - RecordFormDialog (formulário de registro)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Users, Info, X, UserX, Flag, Calendar } from "lucide-react";
import { useTranslation } from "../translations";
import RecordFormDialog from "./RecordFormDialog";
import CalendarEventDialog from "./CalendarEventDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente de Tooltip inteligente que se posiciona dinamicamente
// Permite hover no próprio tooltip para scroll
const SmartTooltip = ({ children, content, dayIndex }) => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = React.useRef(null);
  
  // Calcular posição baseada na coluna do dia (0-6)
  const column = dayIndex % 7;
  const row = Math.floor(dayIndex / 7);

  if (!content) return children;

  // Determinar a classe de posicionamento baseada na coluna e linha
  const isRightSide = column >= 5;
  const isTopRow = row <= 1;

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    // Delay de 300ms antes de esconder para permitir mover o mouse para o tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className={`
            absolute z-[9999]
            bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64
            ${isRightSide ? 'right-full mr-2' : 'left-full ml-2'}
            ${isTopRow ? 'top-0' : 'bottom-0'}
          `}
          style={{
            minWidth: '260px',
            maxHeight: '350px',
            overflowY: 'auto'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>
      )}
    </div>
  );
};

// Componente de dica para múltiplos colaboradores
const MultiWorkerTip = ({ onDismiss, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
          <Users className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
              👥 {t('multiWorkerTipTitle')}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-blue-500 hover:text-blue-700">
                      <Info className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs bg-white p-3 shadow-lg border">
                    <p className="text-sm text-gray-700">
                      {t('multiWorkerTipTooltip')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h4>
            <button 
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600 p-1"
              title={t('close') || 'Fechar'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <p className="text-sm text-blue-700 mt-1">
            {t('multiWorkerTipDesc')}
          </p>
          
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-blue-600 hover:text-blue-800 mt-2 font-medium underline"
          >
            {isExpanded ? `▲ ${t('multiWorkerTipHide')}` : `▼ ${t('multiWorkerTipToggle')}`}
          </button>
          
          {isExpanded && (
            <div className="mt-3 bg-white rounded-lg p-3 border border-blue-100 space-y-2">
              <div className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <p className="text-xs text-gray-700">{t('multiWorkerTipStep1')}</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <p className="text-xs text-gray-700">{t('multiWorkerTipStep2')} <strong>{t('multiWorkerTipStep2Bold')}</strong> {t('multiWorkerTipStep2End')}</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <p className="text-xs text-gray-700">{t('multiWorkerTipStep3')} <strong>&quot;{t('multiWorkerTipStep3Bold')}&quot;</strong></p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                <p className="text-xs text-gray-700">
                  {t('multiWorkerTipStep4')} <strong className="text-green-600">&quot;👥 {t('multiWorkerTipStep4Bold')}&quot;</strong>
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">5</span>
                <p className="text-xs text-gray-700">{t('multiWorkerTipStep5')}</p>
              </div>
              
              <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
                <span className="bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">2</span>
                <span>= {t('multiWorkerTipNote')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MonthlyCalendarView = ({ property, month, records, onUpdate }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [existingDayRecords, setExistingDayRecords] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]); // faltas + feriados do mês
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventDialogDate, setEventDialogDate] = useState(null);
  const [showMultiWorkerTip, setShowMultiWorkerTip] = useState(() => {
    // Verifica localStorage para não mostrar a dica repetidamente
    const dismissed = localStorage.getItem('multiWorkerTipDismissed');
    return !dismissed;
  });
  const [showLegendTip, setShowLegendTip] = useState(() => {
    const dismissed = localStorage.getItem('calendarLegendDismissed');
    return !dismissed;
  });

  const handleDismissTip = () => {
    setShowMultiWorkerTip(false);
    localStorage.setItem('multiWorkerTipDismissed', 'true');
  };

  const handleDismissLegend = () => {
    setShowLegendTip(false);
    localStorage.setItem('calendarLegendDismissed', 'true');
  };

  // Buscar eventos do calendário (faltas/feriados) do mês
  const fetchCalendarEvents = async () => {
    if (!property?.id || !month) return;
    try {
      const res = await axios.get(`${API}/calendar-events`, {
        params: { property_id: property.id, month }
      });
      setCalendarEvents(res.data || []);
    } catch (err) {
      console.error("Erro ao buscar eventos do calendário:", err);
      setCalendarEvents([]);
    }
  };

  useEffect(() => {
    fetchCalendarEvents();
  }, [property?.id, month]);

  // Lista de trabalhadores únicos para autocomplete
  const uniqueWorkers = Array.from(
    new Set((records || []).map(r => r.trabalhador_responsavel).filter(Boolean))
  ).sort();

  console.log("MonthlyCalendarView rendered with:", { 
    property: property?.name, 
    month, 
    recordsCount: records?.length || 0 
  });

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const year = parseInt(month.split('-')[0]);
    const monthIndex = parseInt(month.split('-')[1]) - 1;
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday

    const days = [];
    const current = new Date(startDate);
    
    // Generate 6 weeks (42 days) to fill calendar grid
    for (let i = 0; i < 42; i++) {
      const dateStr = current.toISOString().split('T')[0];
      const dayRecords = records.filter(r => r.date === dateStr);
      const dayEvents = calendarEvents.filter(e => e.date === dateStr);
      const isCurrentMonth = current.getMonth() === monthIndex;
      
      days.push({
        date: dateStr,
        day: current.getDate(),
        records: dayRecords,  // Array de registros para este dia
        record: dayRecords[0], // Primeiro registro para compatibilidade
        events: dayEvents,  // Faltas e feriados do dia
        isCurrentMonth
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const calendarDays = generateCalendarGrid();
  const monthName = (() => {
    const [year, monthNum] = month.split('-');
    const monthKeys = ['rptMonthJan','rptMonthFeb','rptMonthMar','rptMonthApr','rptMonthMay','rptMonthJun','rptMonthJul','rptMonthAug','rptMonthSep','rptMonthOct','rptMonthNov','rptMonthDec'];
    return `${t(monthKeys[parseInt(monthNum) - 1])} ${year}`;
  })();

  const handleDayClick = (date, record) => {
    setSelectedDate(date);
    // Buscar registros existentes do dia para formulário simplificado
    const dayRecords = records.filter(r => r.date === date);
    setExistingDayRecords(dayRecords);
    setSelectedRecord(null);  // Sempre criar novo registro
    setShowRecordForm(true);
  };

  // Abrir dialog de Falta/Feriado para um dia específico
  const handleOpenEventDialog = (date, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEventDialogDate(date);
    setShowEventDialog(true);
  };

  // Eventos do dia selecionado no event dialog
  const eventDialogEvents = eventDialogDate
    ? calendarEvents.filter(ev => ev.date === eventDialogDate)
    : [];

  const getActivityIcons = (record) => {
    if (!record) return null;
    
    const icons = [];
    // Usar imagem da faca de sangria real
    if (record.sangria) icons.push(<img key="sangria" src="/icons/faca_sangria.png" alt="Sangria" className="inline-block w-5 h-5" title={t('bleedingPerformed')} />);
    if (record.reposicao > 0) icons.push('🔄');
    if (record.sangria_perdida > 0) icons.push('❌');
    if (record.fungicida) icons.push('🧪');
    if (record.ethrel) icons.push('⚡');
    if (record.chuva_mm > 0) icons.push('🌧️');
    
    return icons.slice(0, 3); // Show max 3 icons
  };

  return (
    <div className="hevea-calendar hevea-fade-in">
      {/* Dica para múltiplos colaboradores */}
      {showMultiWorkerTip && (
        <MultiWorkerTip onDismiss={handleDismissTip} t={t} />
      )}
      
      <div className="hevea-calendar-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold capitalize">{monthName}</h3>
            <p className="text-sm text-green-100 font-medium">{property.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Botão para reexibir a dica se foi fechada */}
            {!showMultiWorkerTip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={() => setShowMultiWorkerTip(true)}
                      className="bg-green-600 hover:bg-green-500 text-white rounded-full p-2 transition-colors"
                    >
                      <Users className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="bg-white text-gray-800 p-2 shadow-lg border">
                    <p className="text-sm">{t('multiWorkerTipReshow')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Legenda explicativa do calendário */}
      {showLegendTip && (
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <img src="/icons/faca_sangria.png" alt="" className="w-4 h-4" />
                <span className="text-gray-700">{t('legendBleeding') || 'Sangria'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-red-600 font-bold">✕</span>
                <span className="text-gray-700">{t('legendLost') || 'Sangria perdida (chuva)'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <UserX className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-gray-700">{t('legendAbsence') || 'Falta de trabalhador'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Flag className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-gray-700">{t('legendHoliday') || 'Feriado'}</span>
              </div>
            </div>
            <button
              onClick={handleDismissLegend}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              title={t('close') || 'Fechar'}
              data-testid="dismiss-legend-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1.5">
            💡 {t('legendHint') || 'Clique num dia para registrar sangria. Para marcar falta/feriado, use o ícone no canto da célula.'}
          </p>
        </div>
      )}
      
      {/* Week Days Header */}
      <div className="grid grid-cols-7 border-b border-green-100">
        {['weekDaySun', 'weekDayMon', 'weekDayTue', 'weekDayWed', 'weekDayThu', 'weekDayFri', 'weekDaySat'].map(key => (
          <div key={key} className="p-2 text-center text-sm font-medium text-gray-600 bg-gray-50">
            {t(key)}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 overflow-visible">
        {calendarDays.map(({ date, day, record, records, events, isCurrentMonth }, index) => {
          const activityIcons = getActivityIcons(record);
          const hasProduction = record?.producao_kg > 0;
          const multipleWorkers = records.length > 1;
          const totalProduction = records.reduce((sum, r) => sum + (r.producao_kg || 0), 0);
          const dayFaltas = (events || []).filter(e => e.event_type === 'falta');
          const dayFeriado = (events || []).find(e => e.event_type === 'feriado');
          const hasAdminEvent = dayFaltas.length > 0 || !!dayFeriado;
          
          // Agrupar informações do dia (incluindo reposições, sangrias, etc.)
          const lotesInfo = records.filter(r => 
            r.talhao_lote_sangria || r.producao_kg > 0 || r.reposicao > 0 || r.sangria || r.fungicida || r.ethrel
          ).map(r => ({
            lote: r.talhao_lote_sangria || (r.sangria ? t('mcvBleedingPerformed') : r.reposicao > 0 ? `${r.reposicao} ${t('mcvReplacements')}` : t('mcvActivityRegistered')),
            producao: r.producao_kg || 0,
            trabalhador: r.trabalhador_responsavel || 'N/A',
            reposicao: r.reposicao || 0,
            sangria: r.sangria,
            fungicida: r.fungicida,
            ethrel: r.ethrel
          }));

          // Conteúdo do tooltip - Resumo compacto + detalhes com scroll
          const workersCount = lotesInfo.filter(i => i.trabalhador && i.trabalhador !== 'N/A').length;
          const tooltipContent = (lotesInfo.length > 0 || hasAdminEvent) ? (
            <>
              {/* Cabeçalho com resumo */}
              <div className="text-xs font-semibold text-gray-800 mb-2 pb-2 border-b">
                <div className="flex justify-between items-center">
                  <span>📋 {t('mcvTooltipDay')} {day}/{month.split('-')[1]}</span>
                </div>
                {(workersCount > 0 || totalProduction > 0) && (
                  <div className="flex gap-3 mt-1 text-xs font-medium">
                    {workersCount > 0 && (
                      <span className="text-indigo-600">👷 {workersCount} {workersCount > 1 ? t('mcvWorkerPlural') : t('mcvWorkerSingle')}</span>
                    )}
                    {totalProduction > 0 && (
                      <span className="text-green-600">📦 {totalProduction.toFixed(1)} kg</span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Faltas e Feriado do dia (no topo, antes da lista de sangrias) */}
              {(dayFaltas.length > 0 || dayFeriado) && (
                <div className="space-y-1.5 mb-2">
                  {dayFeriado && (
                    <div className="text-xs bg-gray-100 border border-gray-200 rounded p-2">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700">
                        <Flag className="w-3.5 h-3.5" />
                        <span>{t('celHoliday') || 'Feriado'}</span>
                        {dayFeriado.description && (
                          <span className="text-gray-500 font-normal">— {dayFeriado.description}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {dayFaltas.map((f) => (
                    <div key={f.id} className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                      <div className="flex items-center gap-1.5 font-medium text-amber-900">
                        <UserX className="w-3.5 h-3.5" />
                        <span>{t('celAbsence') || 'Falta'}:</span>
                        <span className="font-semibold">{f.trabalhador_responsavel}</span>
                        {f.compensada && (
                          <span className="text-green-700 text-[10px]">✓ {t('celCompensated') || 'compensada'}</span>
                        )}
                      </div>
                      {f.motivo && (
                        <div className="text-[10px] text-amber-700 mt-0.5 ml-5">{f.motivo}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Lista de trabalhadores com scroll */}
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {lotesInfo.map((info, idx) => (
                  <div key={idx} className="text-xs bg-green-50 rounded p-2">
                    {info.trabalhador && info.trabalhador !== 'N/A' && (
                      <div className="font-semibold text-gray-700 mb-1">👷 {info.trabalhador}</div>
                    )}
                    {info.sangria && (
                      <div className="font-medium text-green-800 flex items-center gap-1">
                        <img src="/icons/faca_sangria.png" alt="" className="w-4 h-4 inline" /> {info.lote}
                      </div>
                    )}
                    {info.reposicao > 0 && (
                      <div className="font-medium text-blue-700">🔄 {info.reposicao} {t('mcvReplacements')}</div>
                    )}
                    {info.fungicida && (
                      <div className="font-medium text-purple-700">🧪 {t('mcvFungicide')}</div>
                    )}
                    {info.ethrel && (
                      <div className="font-medium text-orange-700">⚡ {t('mcvEthrel')}</div>
                    )}
                    {info.producao > 0 && (
                      <div className="text-green-600">📦 {info.producao.toFixed(1)} kg</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : null;
          
          return (
            <SmartTooltip key={date} content={tooltipContent} dayIndex={index}>
              <div
                className={`hevea-calendar-day cursor-pointer relative ${
                  !isCurrentMonth ? 'text-gray-400 bg-gray-50' : ''
                } ${record ? 'hevea-activity-sangria' : ''} ${
                  multipleWorkers ? 'border-2 border-blue-300' : ''
                } ${dayFeriado ? 'bg-gray-100' : ''}`}
                onClick={() => isCurrentMonth && handleDayClick(date, record)}
                data-testid={`calendar-day-${date}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{day}</span>
                  {/* Ícone discreto para marcar falta/feriado */}
                  {isCurrentMonth && (
                    <button
                      type="button"
                      onClick={(e) => handleOpenEventDialog(date, e)}
                      className="opacity-30 hover:opacity-100 text-gray-500 hover:text-amber-600 transition-opacity p-0.5"
                      title={t('celMarkEvent') || 'Marcar falta ou feriado'}
                      data-testid={`mark-event-btn-${date}`}
                    >
                      <Calendar className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                {/* Multiple Workers Indicator */}
                {multipleWorkers && (
                  <div className="absolute top-1 right-6 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {records.length}
                  </div>
                )}
                
                {/* Activity Icons */}
                {activityIcons && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {activityIcons.map((icon, idx) => (
                      <span key={idx} className="text-xs">{icon}</span>
                    ))}
                  </div>
                )}
                
                {/* Production Info - Show total if multiple workers */}
                {(hasProduction || totalProduction > 0) && (
                  <div className="text-xs font-medium text-green-700">
                    {multipleWorkers ? `${totalProduction.toFixed(1)}kg` : `${record.producao_kg}kg`}
                  </div>
                )}

                {/* Badges de Falta/Feriado */}
                {(dayFaltas.length > 0 || dayFeriado) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {dayFeriado && (
                      <span
                        className="inline-flex items-center gap-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold px-1 py-0.5 rounded"
                        title={dayFeriado.description || (t('celHoliday') || 'Feriado')}
                      >
                        <Flag className="w-2.5 h-2.5" />
                        FER
                      </span>
                    )}
                    {dayFaltas.slice(0, 2).map((f) => (
                      <span
                        key={f.id}
                        className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded ${
                          f.compensada ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                        }`}
                        title={`${f.trabalhador_responsavel}${f.motivo ? ' - ' + f.motivo : ''}${f.compensada ? ' (compensada)' : ''}`}
                      >
                        <UserX className="w-2.5 h-2.5" />
                        {(f.trabalhador_responsavel || '').split(' ')[0].substring(0, 6)}
                        {f.compensada && '✓'}
                      </span>
                    ))}
                    {dayFaltas.length > 2 && (
                      <span className="text-[9px] text-amber-700 font-medium">
                        +{dayFaltas.length - 2}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Add Button for Empty Days */}
                {records.length === 0 && !hasAdminEvent && isCurrentMonth && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-green-50 transition-opacity pointer-events-none">
                    <Plus className="w-4 h-4 text-green-600" />
                  </div>
                )}
              </div>
            </SmartTooltip>
          );
        })}
      </div>
      
      {/* Record Form Dialog */}
      <RecordFormDialog
        open={showRecordForm}
        onClose={() => setShowRecordForm(false)}
        property={property}
        date={selectedDate}
        existingRecord={selectedRecord}
        existingDayRecords={existingDayRecords}
        onRefresh={onUpdate}
        onComplete={() => {
          onUpdate();
          setShowRecordForm(false);
        }}
      />

      {/* Calendar Event Dialog (Falta/Feriado) */}
      <CalendarEventDialog
        open={showEventDialog}
        onClose={() => setShowEventDialog(false)}
        property={property}
        date={eventDialogDate}
        workers={uniqueWorkers}
        existingEvents={eventDialogEvents}
        onSaved={() => {
          fetchCalendarEvents();
          // Não fecha automaticamente: usuário pode adicionar mais eventos ou remover
        }}
      />
    </div>
  );
};

export default MonthlyCalendarView;
